<template>
  <v-container fluid class="home-assets pa-2">
    <div class="pa-2">
      <v-text-field
        v-model="search"
        dense
        outlined
        clearable
        hide-details
        label="Search asset"
        prepend-inner-icon="mdi-magnify"
        color="primary"
      ></v-text-field>
    </div>

    <v-sheet rounded>
      <div v-if="loading && !filteredGroups.length" class="pa-8 text-center grey--text">
        <v-progress-circular indeterminate color="primary" size="42"></v-progress-circular>
        <div class="mt-3">Processing…</div>
      </div>

      <v-list v-else-if="filteredGroups.length" nav>
        <v-list-group
          v-for="group in filteredGroups"
          :key="group.text"
          v-model="group.model"
          active-class="primary--text"
          :ripple="false"
        >
          <template v-slot:activator>
            <v-list-item-content>
              <v-list-item-title class="font-weight-medium">
                {{ group.text }} ({{ group.children.length }})
              </v-list-item-title>
            </v-list-item-content>
          </template>

          <div class="home-assets__list">
            <template v-for="asset in group.children">
              <v-list-item
                :key="asset.imei"
                two-line
                @click="openAsset(group, asset)"
              >
                <v-list-item-avatar size="36">
                  <v-icon :color="asset.loc_valid == 1 ? 'primary' : 'grey'">mdi-car</v-icon>
                </v-list-item-avatar>

                <v-list-item-content>
                  <v-list-item-title>
                    {{ asset.plate_number || asset.name }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    {{ asset.current_loc || 'No location' }}
                  </v-list-item-subtitle>
                </v-list-item-content>

                <v-list-item-action>
                  <v-list-item-action-text>
                    {{ asset.loc_valid == 1 ? lastSeen(asset) : 'No Data' }}
                  </v-list-item-action-text>
                  <v-list-item-action-text>
                    {{ asset.speed }} KPH
                  </v-list-item-action-text>
                </v-list-item-action>

                <v-list-item-action>
                  <v-icon small>mdi-chevron-right</v-icon>
                </v-list-item-action>
              </v-list-item>
              <v-divider :key="`div-${asset.imei}`"></v-divider>
            </template>
          </div>
        </v-list-group>
      </v-list>

      <div v-else class="pa-8 text-center grey--text">
        No assets available.
      </div>
    </v-sheet>
  </v-container>
</template>

<script>
import { mapState, mapActions } from "vuex";
import { getGroupedAssets } from "@/services/assetsDb";
import { relativeAge } from "@/views/mixins/kensrin_map";

export default {
  name: "HomeAssets",
  data() {
    return {
      all_assets: [],
      asset_groups: [],
      search: "",
      loading: false,
      agetimer: null,
      nowMs: Date.now(),
    };
  },
  computed: {
    ...mapState(["session", "selected_group", "assets_version"]),
    filteredGroups() {
      const term = String(this.search || "").trim().toLowerCase();
      if (!term) return this.asset_groups;

      return this.asset_groups
        .map((group) => ({
          ...group,
          children: group.children.filter(
            (a) =>
              String(a.name || "").toLowerCase().includes(term) ||
              String(a.plate_number || "").toLowerCase().includes(term) ||
              String(a.imei || "").toLowerCase().includes(term)
          ),
        }))
        .filter((group) => group.children.length > 0);
    },
  },
  created() {
    // This asset-list page is a mobile / tablet landing only. On desktop the
    // permanent sidebar already provides it, so send those users to the map.
    if (typeof window !== "undefined" && window.innerWidth >= 990) {
      this.$router.replace("/account/dashboard");
    }
  },
  async mounted() {
    await this.loadAssets();
    // Keep the "last seen" labels ageing while this page stays open (an idle
    // asset is dropped from the delta poll, so nothing else would re-render).
    this.agetimer = window.setInterval(() => {
      this.nowMs = Date.now();
    }, 30000);
  },
  beforeDestroy() {
    clearInterval(this.agetimer);
  },
  watch: {
    // The central poller bumps this after each /objects sync.
    assets_version() {
      this.loadAssets();
    },
  },
  methods: {
    ...mapActions(["select_object", "select_active_group"]),
    // Live "last seen" label: never render the server's frozen relative string.
    lastSeen(asset) {
      return relativeAge(asset, this.nowMs);
    },
    async loadAssets() {
      this.loading = true;
      try {
        if (!this.session.account || !this.session.account[0]) return;
        const groups = await getGroupedAssets(this.session.account[0].username);

        // Keep the currently selected group expanded, else open the first one.
        const openName = this.selected_group || (groups[0] && groups[0].text);
        groups.forEach((g) => {
          g.model = g.text === openName;
        });

        this.all_assets = groups;
        this.asset_groups = groups;
      } finally {
        this.loading = false;
      }
    },
    openAsset(group, asset) {
      this.select_active_group(group.text);
      this.select_object([asset.imei]);
      this.$router.push("/account/dashboard");
    },
  },
};
</script>

<style scoped>
.home-assets {
  background: #eee;
  min-height: 100%;
}

.home-assets .v-list-item {
  cursor: pointer;
}

.home-assets__list {
  max-height: 55vh;
  overflow-y: auto;
}
</style>
